import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
  Alert,
} from 'react-bootstrap';
import { createTooltipProps } from '../utils/fieldTooltips';
import MissingSelectOption from '../components/MissingSelectOption';
import GridPagination from '../components/GridPagination';

function InclusionModal({ inclusion, onClose, onSave, error }) {
  const [formData, setFormData] = useState({
    parent_type: '',
    parent_code: '',
    incl_cat: '',
    incl_stockno: '',
    order_num: '',
    quantity: '',
    include_flag: false,
    no_charge: false,
    sales_inclusion_method: '',
  });

  // State for dropdown options
  const [parentCodes, setParentCodes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stockNumbers, setStockNumbers] = useState([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState('');

  // Fetch functions
  const fetchParentCodes = async (parentType) => {
    if (!parentType) {
      setParentCodes([]);
      return;
    }

    console.log('Fetching parent codes for type:', parentType);

    try {
      let allItems = [];

      if (parentType === 'C') {
        try {
          const hireResponse = await axios.get('/api/hire-categories/codes');
          allItems = (hireResponse?.data || []).map((item) => ({
            code: item.code,
            name: item.name,
          }));
        } catch (error) {
          console.error('Error fetching hire categories:', error);
        }
      } else if (parentType === 'S') {
        try {
          const hireStockResponse = await axios.get('/api/hire-stock/stock-numbers');
          allItems = (hireStockResponse?.data || []).map((item) => ({
            stock_no: item.stock_no,
            name: item.name,
          }));
        } catch (error) {
          console.error('Error fetching hire stock:', error);
        }
      }

      // Get unique items based on code/stock_no
      const uniqueItems = [];
      const seen = new Set();

      for (const item of allItems) {
        const key = item.code || item.stock_no;
        if (key && !seen.has(key)) {
          seen.add(key);
          uniqueItems.push(item);
        }
      }

      // Sort by code/stock_no
      uniqueItems.sort((a, b) => {
        const aKey = a.code || a.stock_no || '';
        const bKey = b.code || b.stock_no || '';
        return aKey.localeCompare(bKey);
      });

      console.log('Setting parent codes:', uniqueItems.length, 'unique items');
      console.log('First few items:', uniqueItems.slice(0, 3));
      setParentCodes(uniqueItems);
    } catch (error) {
      console.error('Failed to fetch parent codes:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error message:', error.message);
      setParentCodes([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/inclusions/categories');

      // Get unique categories based on NAME (category_name)
      const data = response.data || [];
      const uniqueCategories = [];
      const seen = new Set();

      for (const category of data) {
        if (!seen.has(category.category_name)) {
          seen.add(category.category_name);
          uniqueCategories.push(category);
        }
      }

      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchStockNumbers = async (category) => {
    if (!category) {
      setStockNumbers([]);
      return;
    }

    try {
      const response = await axios.get(`/api/inclusions/category-items/${category}`);

      // Get unique items based on NAME
      const data = response.data || [];
      const uniqueItems = [];
      const seen = new Set();

      for (const item of data) {
        if (!seen.has(item.name)) {
          seen.add(item.name);
          uniqueItems.push(item);
        }
      }

      setStockNumbers(uniqueItems);
    } catch (error) {
      console.error('Failed to fetch stock numbers:', error);
      setStockNumbers([]);
    }
  };

  useEffect(() => {
    if (inclusion) {
      setFormData({
        ...inclusion,
        // API may return integer (1/0) or boolean (true/false) depending on column type
        include_flag: inclusion.include_flag === 1 || inclusion.include_flag === true,
        no_charge: inclusion.no_charge === 1 || inclusion.no_charge === true,
      });
      // Fetch dependent data for editing
      if (inclusion.parent_type) {
        fetchParentCodes(inclusion.parent_type);
      }
      if (inclusion.incl_cat) {
        fetchStockNumbers(inclusion.incl_cat);
      }
    }
    fetchCategories();
  }, [inclusion]);

  // Update category type when categories are loaded or formData.incl_cat changes
  useEffect(() => {
    if (formData.incl_cat && categories.length > 0) {
      const selectedCategory = categories.find((cat) => cat.category_code === formData.incl_cat);
      setSelectedCategoryType(selectedCategory?.type || '');
    } else {
      setSelectedCategoryType('');
    }
  }, [formData.incl_cat, categories]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue,
    });

    // Handle cascading updates
    if (name === 'parent_type') {
      // Reset parent_code when parent_type changes
      setFormData((prev) => ({ ...prev, [name]: newValue, parent_code: '' }));
      fetchParentCodes(newValue);
    } else if (name === 'incl_cat') {
      // Reset incl_stockno when category changes
      setFormData((prev) => ({ ...prev, [name]: newValue, incl_stockno: '' }));
      fetchStockNumbers(newValue);

      // Determine the category type (Sales or Hire)
      const selectedCategory = categories.find((cat) => cat.category_code === newValue);
      setSelectedCategoryType(selectedCategory?.type || '');

      // Reset sales_inclusion_method if switching away from a sales category
      if (selectedCategory?.type !== 'Sales') {
        setFormData((prev) => ({ ...prev, sales_inclusion_method: '' }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>{inclusion ? 'Edit Inclusion' : 'Add New Inclusion'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Parent Type <span className="text-danger">*</span>
                </Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('parent_type')}>
                      Determines whether inclusion applies to hire categories (C) or stock numbers
                      (S)
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="parent_type"
                    value={formData.parent_type}
                    onChange={handleChange}
                    required
                    {...createTooltipProps('parent_type')}
                  >
                    <option value="">Select type...</option>
                    <MissingSelectOption
                      value={formData.parent_type}
                      options={[{ value: 'C' }, { value: 'S' }]}
                      getOptionValue={(option) => option.value}
                    />
                    <option value="C">C - Category (Hire)</option>
                    <option value="S">S - Stock (Hire)</option>
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Parent Code *</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('parent_code')}>
                      Links inclusions to specific hire categories or stock items
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="parent_code"
                    value={formData.parent_code}
                    onChange={handleChange}
                    required
                    disabled={!formData.parent_type}
                    {...createTooltipProps('parent_code')}
                  >
                    <option value="">Select parent code...</option>
                    <MissingSelectOption
                      value={formData.parent_code}
                      options={parentCodes}
                      getOptionValue={(item) =>
                        formData.parent_type === 'S' ? item.stock_no : item.code
                      }
                    />
                    {parentCodes.map((item, index) => {
                      if (formData.parent_type === 'C') {
                        return (
                          <option key={index} value={item.code}>
                            {item.code} - {item.name}
                          </option>
                        );
                      }
                      if (formData.parent_type === 'S') {
                        return (
                          <option key={index} value={item.stock_no}>
                            {item.stock_no} - {item.name}
                          </option>
                        );
                      }
                      return null;
                    })}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Inclusion Category <span className="text-danger">*</span>
                </Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('incl_cat')}>
                      Associates inclusion with equipment category for easier management
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="incl_cat"
                    value={formData.incl_cat}
                    onChange={handleChange}
                    required
                    {...createTooltipProps('incl_cat')}
                  >
                    <option value="">Select category...</option>
                    <MissingSelectOption
                      value={formData.incl_cat}
                      options={categories}
                      getOptionValue={(category) => category.category_code}
                    />
                    {categories.map((category, index) => (
                      <option key={index} value={category.category_code}>
                        {category.category_code} - {category.category_name} ({category.type})
                      </option>
                    ))}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Inclusion Stock No</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('incl_stockno')}>
                      Specific stock item to be included with the parent equipment
                    </Tooltip>
                  }
                >
                  <Form.Select
                    name="incl_stockno"
                    value={formData.incl_stockno}
                    onChange={handleChange}
                    disabled={!formData.incl_cat}
                    {...createTooltipProps('incl_stockno')}
                  >
                    <option value="">Select stock number (optional)...</option>
                    <MissingSelectOption
                      value={formData.incl_stockno}
                      options={stockNumbers}
                      getOptionValue={(item) => item.stock_no}
                    />
                    {stockNumbers.map((item, index) => (
                      <option key={index} value={item.stock_no}>
                        {item.stock_no} - {item.name}
                      </option>
                    ))}
                  </Form.Select>
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Order</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('order_num')}>
                      Controls the display order of inclusions in lists and reports
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="order_num"
                    value={formData.order_num}
                    onChange={handleChange}
                    {...createTooltipProps('order_num')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Quantity</Form.Label>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('quantity')}>
                      Number of items to be included with the parent equipment
                    </Tooltip>
                  }
                >
                  <Form.Control
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    step="0.01"
                    {...createTooltipProps('quantity')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('include_flag')}>
                      Controls whether the inclusion is active and should be included
                    </Tooltip>
                  }
                >
                  <Form.Check
                    type="checkbox"
                    name="include_flag"
                    label="Include?"
                    checked={formData.include_flag}
                    onChange={handleChange}
                    {...createTooltipProps('include_flag')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip {...createTooltipProps('no_charge')}>
                      Determines if the inclusion is provided at no additional cost
                    </Tooltip>
                  }
                >
                  <Form.Check
                    type="checkbox"
                    name="no_charge"
                    label="No Charge?"
                    checked={formData.no_charge}
                    onChange={handleChange}
                    {...createTooltipProps('no_charge')}
                  />
                </OverlayTrigger>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>
              Sales Inclusion Method
              {selectedCategoryType === 'Sales' && <span className="text-danger">*</span>}
            </Form.Label>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip {...createTooltipProps('sales_inclusion_method')}>
                  Method for including items in sales transactions (required for sales categories)
                </Tooltip>
              }
            >
              <Form.Select
                name="sales_inclusion_method"
                value={formData.sales_inclusion_method || ''}
                onChange={handleChange}
                required={selectedCategoryType === 'Sales'}
                disabled={selectedCategoryType !== 'Sales'}
                {...createTooltipProps('sales_inclusion_method')}
              >
                <option value="">Select method...</option>
                <MissingSelectOption
                  value={formData.sales_inclusion_method}
                  options={[{ value: '1' }, { value: '2' }, { value: '3' }, { value: '4' }]}
                  getOptionValue={(option) => option.value}
                />
                <option value="1">1 - Sale Item</option>
                <option value="2">2 - Sale or Return Item</option>
                <option value="3">3 - Sales deferred to offhire</option>
                <option value="4">4 - SoR item not deferred</option>
              </Form.Select>
            </OverlayTrigger>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            {inclusion ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function Inclusions() {
  const [inclusions, setInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingInclusion, setEditingInclusion] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalError, setModalError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchInclusions = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inclusions', {
        params: {
          page,
          limit: pagination.limit,
          search,
        },
      });
      setInclusions(response.data.inclusions);
      setPagination(response.data.pagination);
    } catch (error) {
      setError('Failed to fetch inclusions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInclusions();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInclusions(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchInclusions(newPage, searchTerm);
  };

  const handleAddInclusion = () => {
    setEditingInclusion(null);
    setModalError('');
    setShowModal(true);
  };

  const handleEditInclusion = (inclusion) => {
    setEditingInclusion(inclusion);
    setModalError('');
    setShowModal(true);
  };

  const handleSaveInclusion = async (inclusionData) => {
    try {
      if (editingInclusion) {
        await axios.put(`/api/inclusions/${editingInclusion.id}`, inclusionData);
        setSuccess('Inclusion updated successfully');
      } else {
        await axios.post('/api/inclusions', inclusionData);
        setSuccess('Inclusion created successfully');
      }
      setShowModal(false);
      fetchInclusions(pagination.page, searchTerm);
    } catch (error) {
      setModalError(error.response?.data?.error || 'Failed to save inclusion');
    }
  };

  const handleDeleteInclusion = async (id) => {
    if (window.confirm('Are you sure you want to delete this inclusion?')) {
      try {
        await axios.delete(`/api/inclusions/${id}`);
        setSuccess('Inclusion deleted successfully');
        fetchInclusions(pagination.page, searchTerm);
      } catch (error) {
        setError('Failed to delete inclusion');
      }
    }
  };

  const handleDeleteAllInclusions = async () => {
    if (
      window.confirm(
        'Are you sure you want to delete ALL inclusions? This action cannot be undone!'
      )
    ) {
      if (
        window.confirm(
          'This will permanently delete all your inclusion records. Are you absolutely sure?'
        )
      ) {
        try {
          const response = await axios.delete('/api/inclusions');
          setSuccess(`All inclusions deleted successfully (${response.data.deletedCount} records)`);
          fetchInclusions(1, '');
        } catch (error) {
          setError('Failed to delete all inclusions');
        }
      }
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <i className="fas fa-list-check me-3 text-info" style={{ fontSize: '2.5rem' }}></i>
              <div>
                <h1 className="mb-0">Inclusions</h1>
                <p className="text-muted mb-0">
                  Manage items included with rental categories and stock
                </p>
              </div>
            </div>
            <div style={{ position: 'relative', zIndex: 1 }}>
              <Button variant="primary" onClick={handleAddInclusion} className="me-2">
                Add Inclusion
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteAllInclusions}
                disabled={inclusions.length === 0}
              >
                Delete All
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-danger alert-dismissible">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <div className="alert alert-success alert-dismissible">
              {success}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccess('')}
                aria-label="Close"
              ></button>
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card>
            <Card.Header>
              <Form onSubmit={handleSearch} className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Search by parent code, category, or stock no..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-grow-1"
                />
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Form>
            </Card.Header>

            <Card.Body className="p-0">
              {loading ? (
                <div className="loading">Loading inclusions...</div>
              ) : inclusions.length === 0 ? (
                <div className="empty-state">
                  <p>No inclusions found</p>
                </div>
              ) : (
                <div style={{ paddingLeft: '1rem' }}>
                  <Table responsive hover style={{ marginLeft: 0 }}>
                    <thead>
                      <tr>
                        <th>Parent Type</th>
                        <th>Parent Code</th>
                        <th>Incl. Cat</th>
                        <th>Incl. StockNo</th>
                        <th>Order</th>
                        <th>Quantity</th>
                        <th>Include?</th>
                        <th>No Charge?</th>
                        <th>Sales Method</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inclusions.map((inclusion) => (
                        <tr key={inclusion.id}>
                          <td>{inclusion.parent_type}</td>
                          <td>
                            <strong>{inclusion.parent_code}</strong>
                          </td>
                          <td>{inclusion.incl_cat}</td>
                          <td>{inclusion.incl_stockno}</td>
                          <td>{inclusion.order_num}</td>
                          <td>{inclusion.quantity}</td>
                          <td>
                            <span
                              className={`badge ${inclusion.include_flag ? 'bg-success' : 'bg-secondary'}`}
                            >
                              {inclusion.include_flag ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`badge ${inclusion.no_charge ? 'bg-warning' : 'bg-secondary'}`}
                            >
                              {inclusion.no_charge ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            {inclusion.sales_inclusion_method === '1'
                              ? '1 - Sale Item'
                              : inclusion.sales_inclusion_method === '2'
                                ? '2 - Sale or Return Item'
                                : inclusion.sales_inclusion_method === '3'
                                  ? '3 - Sales deferred to offhire'
                                  : inclusion.sales_inclusion_method === '4'
                                    ? '4 - SoR item not deferred'
                                    : inclusion.sales_inclusion_method || ''}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleEditInclusion(inclusion)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteInclusion(inclusion.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <GridPagination
                    page={pagination.page}
                    pages={pagination.pages}
                    onPageChange={handlePageChange}
                    wrapperClassName="d-flex justify-content-center p-3"
                    ulClassName="mb-0"
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {showModal && (
        <InclusionModal
          inclusion={editingInclusion}
          onClose={() => {
            setShowModal(false);
            setModalError('');
          }}
          onSave={handleSaveInclusion}
          error={modalError}
        />
      )}
    </Container>
  );
}

export default Inclusions;
